import { Link } from "react-router-dom";
import { Scale, ArrowRight, Gift } from "lucide-react";
import { track } from "../lib/api";

/* Conveyancing referral CTA — amber/gold accent so it stands apart from the
   main MortgagePath teal brand. Positioned as an "optional next step".
   MortgagePath is not a conveyancer; it introduces users to a selected partner. */
const AMBER_BG = "#FFF7E6";
const AMBER_BORDER = "#F59E0B";
const AMBER_BUTTON = "#D97706";
const AMBER_BUTTON_HOVER = "#B45309";
const ICON_BADGE_BG = "#FDE7B0";

const buttonStyle = { background: AMBER_BUTTON, color: "#FFFFFF" };
const onButtonEnter = (e) => { e.currentTarget.style.background = AMBER_BUTTON_HOVER; };
const onButtonLeave = (e) => { e.currentTarget.style.background = AMBER_BUTTON; };

const RewardPill = () => (
  <div className="mt-2 inline-flex items-center gap-1.5 rounded-full bg-[#0F766E]/8 border border-[#0F766E]/20 px-2.5 py-1 text-[11px] font-semibold text-[#0F766E]" data-testid="conveyancing-reward-badge">
    <Gift className="w-3 h-3" aria-hidden="true" /> £50 MortgagePath cashback after completion
  </div>
);

const InnerContent = () => (
  <>
    <div className="text-[11px] uppercase tracking-widest font-semibold" style={{ color: AMBER_BUTTON }}>
      Conveyancing quote
    </div>
    <h3 className="font-sora text-xl font-semibold text-[#0B1220] mt-1">
      Need a conveyancing quote?
    </h3>
    <p className="mt-2 text-sm text-[#475569] leading-relaxed">
      Get a no-obligation quote from MortgagePath&apos;s selected conveyancing partner.
    </p>
    <RewardPill />
  </>
);

const FootNote = () => (
  <p className="mt-3 text-xs text-[#475569] leading-relaxed">
    MortgagePath is not a conveyancer and does not provide conveyancing advice. We may receive a
    referral fee if you choose to proceed.
  </p>
);

const IconBadge = () => (
  <span
    className="mt-0.5 w-10 h-10 rounded-xl grid place-items-center shrink-0"
    style={{ background: ICON_BADGE_BG, color: AMBER_BUTTON }}
  >
    <Scale className="w-5 h-5" />
  </span>
);

const ConveyancingCTA = ({ source = "direct", variant = "boxed", className = "" }) => {
  const href = `/conveyancing-quote?source=${encodeURIComponent(source)}`;
  const onCtaClick = () => {
    // Single event per physical click — cta_location captures placement.
    track("conveyancing_cta_clicked", { cta_location: source, cta_text: "Request a conveyancing quote", source });
  };

  // Fully clickable card variant for use inside guides.
  if (variant === "clickable-card") {
    return (
      <Link
        to={href}
        onClick={onCtaClick}
        className={`block group mt-6 rounded-2xl border p-5 hover:shadow-md transition-all ${className}`}
        style={{ background: AMBER_BG, borderColor: AMBER_BORDER }}
        data-testid={`conveyancing-cta-card-${source}`}
      >
        <div className="flex items-start gap-4">
          <IconBadge />
          <div className="flex-1">
            <InnerContent />
            <span
              className="mt-4 inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-semibold shadow-sm"
              style={buttonStyle}
              onMouseEnter={onButtonEnter}
              onMouseLeave={onButtonLeave}
            >
              Request a conveyancing quote <ArrowRight className="w-4 h-4" />
            </span>
            <div className="mt-2 text-xs text-[#475569]">Takes around 60 seconds</div>
            <FootNote />
          </div>
        </div>
      </Link>
    );
  }

  // Subtle variant — same amber palette but toned down. Used at the end of the
  // Snapshot journey where the amber shouldn't dominate the results panel.
  if (variant === "subtle") {
    return (
      <div
        className={`rounded-2xl border p-5 ${className}`}
        style={{ background: "#FFFBEF", borderColor: "#FCD98B" }}
        data-testid={`conveyancing-cta-${source}`}
      >
        <div className="flex items-start gap-3">
          <span
            className="mt-0.5 w-8 h-8 rounded-lg grid place-items-center shrink-0"
            style={{ background: ICON_BADGE_BG, color: AMBER_BUTTON }}
          >
            <Scale className="w-4 h-4" />
          </span>
          <div className="flex-1">
            <div className="text-[11px] uppercase tracking-widest font-semibold" style={{ color: AMBER_BUTTON }}>
              Conveyancing quote
            </div>
            <h3 className="font-sora text-base font-semibold text-[#0B1220] mt-1">
              Need a conveyancing quote?
            </h3>
            <p className="mt-1.5 text-sm text-[#475569] leading-relaxed">
              Get a no-obligation quote from MortgagePath&apos;s selected conveyancing partner.
            </p>
            <RewardPill />
            <Link
              to={href}
              onClick={onCtaClick}
              className="mt-3 inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-semibold shadow-sm transition-colors"
              style={buttonStyle}
              onMouseEnter={onButtonEnter}
              onMouseLeave={onButtonLeave}
              data-testid={`conveyancing-cta-btn-${source}`}
            >
              Request a conveyancing quote <ArrowRight className="w-4 h-4" />
            </Link>
            <div className="mt-2 text-xs text-[#475569]">Takes around 60 seconds</div>
            <p className="mt-2 text-[11px] text-[#94A3B8] leading-relaxed">
              MortgagePath is not a conveyancer and does not provide conveyancing advice.
              We may receive a referral fee if you choose to proceed.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Default boxed CTA — same amber palette, non-clickable card.
  return (
    <div
      className={`rounded-2xl border p-6 sm:p-7 ${className}`}
      style={{ background: AMBER_BG, borderColor: AMBER_BORDER }}
      data-testid={`conveyancing-cta-${source}`}
    >
      <div className="flex items-start gap-4">
        <IconBadge />
        <div className="flex-1">
          <InnerContent />
          <Link
            to={href}
            onClick={onCtaClick}
            className="mt-4 inline-flex items-center gap-1.5 rounded-full px-5 py-2.5 text-sm font-semibold shadow-sm transition-colors"
            style={buttonStyle}
            onMouseEnter={onButtonEnter}
            onMouseLeave={onButtonLeave}
            data-testid={`conveyancing-cta-btn-${source}`}
          >
            Request a conveyancing quote <ArrowRight className="w-4 h-4" />
          </Link>
          <div className="mt-2 text-xs text-[#475569]">Takes around 60 seconds</div>
          <FootNote />
        </div>
      </div>
    </div>
  );
};

export default ConveyancingCTA;
